// src/components/tokenPage/risk/SkewKurtosisCard.tsx

import React, { useMemo, useState } from "react";
import TimeRangePicker from "@/components/common/TimeRangePicker";
import { DataCard } from "@/components/ui";
import { TimeSeriesChart } from "@/components/charts/base";
import { useTokenPrice } from "@/contexts/token";
import { rollingSkewness, rollingKurtosis, toReturns } from "@/lib/metrics";

type Range = '90d' | '180d' | '365d' | 'All';

const RANGE_TO_DAYS: Record<Range, number> = { '90d': 90, '180d': 180, '365d': 365, 'All': Infinity };

const SkewKurtosisCard: React.FC = () => {
  const [range, setRange] = useState<Range>('365d');
  const [tab, setTab] = useState<'skew'|'kurt'>('skew');
  const [win, setWin] = useState<30|90|180|365>(90);
  
  const { token, isLoading } = useTokenPrice();
  const prices = token.prices;

  const { dates, series } = useMemo(() => {
    const d = prices.dates;
    const values = prices.values;
    
    if (d.length === 0 || values.length === 0) {
      return { dates: [], series: [] };
    }
    
    const rets = toReturns(values);
    const minPoints = win === 30 ? 20 : win === 90 ? 60 : win === 180 ? 120 : 180;
    const full = tab === 'skew' 
      ? rollingSkewness(rets, win, minPoints)
      : rollingKurtosis(rets, win, minPoints);
    
    const maxPoints = RANGE_TO_DAYS[range];
    const start = maxPoints === Infinity ? 0 : Math.max(0, d.length - maxPoints);
    const datesAligned = d.slice(start + 1);
    const aligned = full.slice(start, start + datesAligned.length);
    
    // Find first index with valid data
    const firstValidIdx = aligned.findIndex(v => v !== null && Number.isFinite(v));
    if (firstValidIdx >= 0) {
      return { dates: datesAligned.slice(firstValidIdx), series: aligned.slice(firstValidIdx) };
    }
    return { dates: datesAligned, series: aligned };
  }, [prices, range, tab, win]);

  // Hide if no data available (after loading)
  const hasValidData = series.some(v => v !== null && Number.isFinite(v));
  if (!isLoading && !hasValidData) {
    return null;
  }

  return (
    <DataCard
      title="Rolling Skewness / Kurtosis"
      subtitle="Higher moments of returns"
      icon="sliders"
      iconColor="text-purple-400"
      isLoading={isLoading}
      loadingVariant="chart"
      actions={
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-400">Window</span>
          <button className={`text-xs px-2 py-1 rounded-md ${win===30?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(30)}>30d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===90?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(90)}>90d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===180?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(180)}>180d</button>
          <button className={`text-xs px-2 py-1 rounded-md ${win===365?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setWin(365)}>365d</button>
          <span className="text-[11px] text-slate-500 mx-1">|</span>
          <TimeRangePicker options={['90d','180d','365d','All'] as const} value={range} onChange={setRange} theme="blue" />
        </div>
      }
    >
      <div className="mb-2">
        <button className={`text-xs px-2 py-1 rounded-md ${tab==='skew'?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setTab('skew')}>Skew</button>
        <button className={`text-xs px-2 py-1 rounded-md ml-2 ${tab==='kurt'?'bg-slate-700 text-slate-200':'bg-slate-800 text-slate-400'}`} onClick={() => setTab('kurt')}>Kurtosis</button>
      </div>
      <TimeSeriesChart
        dates={dates}
        series={[{ name: `${tab==='skew'?'Skew':'Kurtosis'} ${win}d`, values: series, color: '#38bdf8', areaStyle: true }]}
        height={380}
        yAxisFormat="ratio"
        showLegend={false}
      />
    </DataCard>
  );
};

export default SkewKurtosisCard;
